import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withLocalize } from 'react-localize-redux';
import Spinner from '../../components/widgets/Spinner';
import HelpMenu from '../../components/molecules/HelpMenu';
import Button from '../../components/atoms/Button';
import SettingCard from './components/SettingCard';
import AndroidValidationOverview from './components/AndroidValidationOverview';
import PublisherBlacklistDownload from './components/PublisherBlacklistDownload';
import { fetchFraudSettings, saveFraudSettings } from './service';
import { FAQ_LINK, SETTINGS, SETTING_IDS, TRANSLATION_KEY } from './constants';
import {
    applyChange,
    computeImpersonatingWarning,
    getVisibleSettings,
    hasPendingChanges,
    hasPublisherBlacklists,
    isChangeToReject,
    resolveSettingValue,
} from './utils';
import css from './style.css';

const SAVE_NOTICE_TIMEOUT_MS = 2500;

const EMPTY_META = {
    rejectSaveDisabled: false,
    betaHiddenFlags: {},
    blacklists: [],
    androidValidation: { sites_with_keys: [], sites_without_keys: [], not_supporting_sites: [] },
};

const FraudSettings = ({ translate = () => {} }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({});
    const [changes, setChanges] = useState({});
    const [meta, setMeta] = useState(EMPTY_META);
    const [saveNotice, setSaveNotice] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [saveError, setSaveError] = useState(false);

    useEffect(() => {
        let cancelled = false;
        fetchFraudSettings()
            .then(value => {
                if (cancelled) return;
                setSettings(value.settings || {});
                setMeta({
                    rejectSaveDisabled: !!value.reject_save_disabled,
                    betaHiddenFlags: value.beta_hidden_flags || {},
                    blacklists: value.blacklists || [],
                    androidValidation: value.android_install_validation || EMPTY_META.androidValidation,
                });
                setLoadError(false);
            })
            .catch(error => {
                // Surface an error rather than silently rendering default actions.
                if (cancelled) return;
                console.error('Failed to load fraud settings', error);
                setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    // Auto-dismiss the "Settings Saved." notice after a short delay (legacy parity).
    useEffect(() => {
        if (!saveNotice) return undefined;
        const timer = setTimeout(() => setSaveNotice(false), SAVE_NOTICE_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, [saveNotice]);

    const impersonatingWarning = useMemo(() => computeImpersonatingWarning(changes, meta.rejectSaveDisabled), [
        changes,
        meta.rejectSaveDisabled,
    ]);
    const changed = hasPendingChanges(changes);
    const visibleSettings = useMemo(() => getVisibleSettings(SETTINGS, meta.betaHiddenFlags), [meta.betaHiddenFlags]);
    const showBlacklistDownload = useMemo(() => hasPublisherBlacklists(meta.blacklists, meta.betaHiddenFlags), [
        meta.blacklists,
        meta.betaHiddenFlags,
    ]);

    const handleChange = useCallback(
        (id, value) => {
            setChanges(prev => applyChange(prev, settings, id, value));
            setSaveNotice(false);
            setSaveError(false);
        },
        [settings]
    );

    const handleCancel = useCallback(() => setChanges({}), []);

    const handleSave = useCallback(() => {
        setSaving(true);
        setSaveError(false);
        saveFraudSettings(changes)
            .then(() => {
                setSettings(prev => ({ ...prev, ...changes }));
                setChanges({});
                setSaveNotice(true);
            })
            .catch(error => {
                console.error('Failed to save fraud settings', error);
                setSaveError(true);
            })
            .finally(() => setSaving(false));
    }, [changes]);

    const renderExtra = useCallback(
        setting => {
            if (setting.id === SETTING_IDS.BLACKLISTED_PUBLISHERS && showBlacklistDownload) {
                return <PublisherBlacklistDownload />;
            }
            if (setting.id === SETTING_IDS.ANDROID_INSTALL_VALIDATION) {
                return <AndroidValidationOverview androidValidation={meta.androidValidation} translate={translate} />;
            }
            return null;
        },
        [showBlacklistDownload, meta.androidValidation, translate]
    );

    if (loading) {
        return (
            <div className={css.page}>
                <div className={css.spinnerWrapper}>
                    <Spinner show />
                </div>
            </div>
        );
    }

    const header = (
        <div className={css.header}>
            <h1 className={css.title}>{translate('STATIC.PAGE_HEADERS.FRAUD_GLOBAL_SETTINGS')}</h1>
            <HelpMenu faqLink={FAQ_LINK} />
        </div>
    );

    if (loadError) {
        return (
            <div className={css.page}>
                {header}
                <div className={css.notification}>{translate(`${TRANSLATION_KEY}.LOAD_ERROR`)}</div>
            </div>
        );
    }

    return (
        <div className={css.page}>
            {header}

            {impersonatingWarning && (
                <div className={css.notification}>{translate(`${TRANSLATION_KEY}.IMPERSONATING_WARNING`)}</div>
            )}
            {saveError && <div className={css.notification}>{translate(`${TRANSLATION_KEY}.SAVE_ERROR`)}</div>}
            {saveNotice && (
                <div className={classNames(css.notification, css.info)}>
                    {translate(`${TRANSLATION_KEY}.SAVED_NOTICE`)}
                </div>
            )}

            <div className={css.settingsList}>
                {visibleSettings.map(setting => (
                    <SettingCard
                        key={setting.id}
                        setting={setting}
                        value={resolveSettingValue(setting, settings, changes)}
                        warning={impersonatingWarning && isChangeToReject(changes, setting.id)}
                        onChange={handleChange}
                        translate={translate}
                        extra={renderExtra(setting)}
                    />
                ))}
            </div>

            <div className={css.footer}>
                <Button type="link" text="STATIC.BUTTONS.CANCEL" disabled={!changed || saving} onClick={handleCancel} />
                <Button
                    type="primary"
                    text="STATIC.BUTTONS.SAVE"
                    showSpinner={saving}
                    disabled={!changed || saving || impersonatingWarning}
                    onClick={handleSave}
                />
            </div>
        </div>
    );
};

FraudSettings.propTypes = {
    translate: PropTypes.func,
};

export default withLocalize(FraudSettings);
